import { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { JsonEditor } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';

interface PasswordUpdate {
  email: string;
  password: string;
}

interface UpdatePasswordsFormData {
  passwordUpdates: string;
}

interface OperationResult {
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  passwordsUpdated: number;
  errors?: Array<{
    email: string;
    error: string;
  }>;
  skipped?: Array<{
    email: string;
    reason: string;
  }>;
}

interface ProgressInfo {
  current: number;
  total: number;
  currentEmail: string;
}

interface UpdateClientPasswordsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_JSON = JSON.stringify(
  [
    { email: 'cliente1@example.com', password: '15031990' },
    { email: 'cliente2@example.com', password: '22071985' },
  ],
  null,
  2
);

export default function UpdateClientPasswordsModal({
  isOpen,
  onClose,
}: UpdateClientPasswordsModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [operationResults, setOperationResults] = useState<OperationResult | null>(null);
  const [progressInfo, setProgressInfo] = useState<ProgressInfo | null>(null);
  const [formData, setFormData] = useState<UpdatePasswordsFormData>({
    passwordUpdates: DEFAULT_JSON,
  });

  const handleClose = () => {
    onClose();
    setFormData({ passwordUpdates: DEFAULT_JSON });
    setShowResults(false);
    setOperationResults(null);
    setProgressInfo(null);
  };

  const handleEditorChange = (value: string) => {
    setFormData({ passwordUpdates: value });
  };

  const executeOperation = async () => {
    // Validar JSON
    let passwordUpdates: PasswordUpdate[];
    try {
      passwordUpdates = JSON.parse(formData.passwordUpdates);
    } catch (error) {
      toast.error('Erro', 'JSON inválido. Verifique a sintaxe.');
      return;
    }

    // Validar que é um array
    if (!Array.isArray(passwordUpdates)) {
      toast.error('Erro', 'O JSON deve ser um array de objetos');
      return;
    }

    // Validar estrutura de cada item
    for (const item of passwordUpdates) {
      if (!item.email || !item.password) {
        toast.error('Erro', 'Cada objeto deve ter as propriedades "email" e "password"');
        return;
      }
    }

    if (passwordUpdates.length === 0) {
      toast.error('Erro', 'Adicione pelo menos um cliente para atualizar');
      return;
    }

    setIsLoading(true);
    setShowResults(true);

    // Inicializar estatísticas
    setOperationResults({
      totalProcessed: 0,
      successCount: 0,
      errorCount: 0,
      skippedCount: 0,
      passwordsUpdated: 0,
    });

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const url = `${API_URL}/operations/update-client-passwords/stream?passwordUpdates=${encodeURIComponent(JSON.stringify(passwordUpdates))}`;

      const eventSource = new EventSource(url, { withCredentials: true });
      let hasReceivedData = false;

      eventSource.onmessage = (event) => {
        try {
          hasReceivedData = true;
          const data = JSON.parse(event.data);

          if (data.type === 'init') {
            setProgressInfo({
              current: 0,
              total: data.totalClients,
              currentEmail: '',
            });
          } else if (data.type === 'progress') {
            // Atualizar progresso
            setProgressInfo({
              current: data.current,
              total: data.total,
              currentEmail: data.email,
            });

            // Atualizar estatísticas em tempo real
            setOperationResults({
              totalProcessed: data.stats.totalProcessed,
              successCount: data.stats.successCount,
              errorCount: data.stats.errorCount,
              skippedCount: data.stats.skippedCount,
              passwordsUpdated: data.stats.passwordsUpdated,
            });
          } else if (data.type === 'complete') {
            // Resultado final
            setOperationResults(data.result);
            setIsLoading(false);
            eventSource.close();
            toast.success('Sucesso', 'Operação concluída com sucesso!');
          } else if (data.type === 'error') {
            setIsLoading(false);
            eventSource.close();
            toast.error('Erro', data.error || 'Erro ao executar operação');
          }
        } catch (err) {
          console.error('Erro ao processar evento SSE:', err);
        }
      };

      eventSource.onerror = () => {
        if (!hasReceivedData) {
          toast.error(
            'Erro',
            'Não foi possível conectar ao servidor. Verifique se a API está rodando.'
          );
        } else {
          toast.error('Erro', 'Conexão interrompida com o servidor');
        }

        setIsLoading(false);
        eventSource.close();
      };
    } catch (error) {
      console.error('Erro ao executar operação:', error);
      setIsLoading(false);
      toast.error('Erro', 'Erro ao executar operação. Verifique os logs.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogClose onClick={handleClose} />
        <DialogHeader>
          <DialogTitle>Atualizar Senhas dos Clientes</DialogTitle>
          <DialogDescription>
            Atualiza senhas de clientes baseado em email e nova senha
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {!isLoading && !showResults ? (
            <>
              <div>
                <JsonEditor
                  label="Array de Atualizações (JSON)"
                  height="400px"
                  value={formData.passwordUpdates}
                  onChange={handleEditorChange}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Formato: Array de objetos com <code className="text-xs">email</code> e{' '}
                  <code className="text-xs">password</code>
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  O que esta operação faz:
                </h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>Para cada item do array, busca o usuário pelo email no banco de dados</li>
                  <li>Se o email existir e for um cliente, atualiza a senha</li>
                  <li>Se o email não existir ou não for cliente, pula (não gera erro)</li>
                  <li>Senhas são hashadas com bcrypt antes de serem salvas</li>
                  <li>Exibe estatísticas em tempo real: sucessos, erros e pulados</li>
                </ul>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                  ⚠️ Importante:
                </h4>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
                  <li>
                    Formato da senha: DDMMYYYY (8 dígitos) para senhas baseadas em data de
                    nascimento
                  </li>
                  <li>Exemplo: nascido em 15/03/1990 → senha "15031990"</li>
                  <li>Apenas usuários com role CLIENT serão atualizados</li>
                  <li>Emails não encontrados ou que não são clientes serão pulados</li>
                </ul>
              </div>
            </>
          ) : showResults ? (
            <div className="space-y-4">
              {/* Barra de Progresso */}
              {isLoading && progressInfo && (
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Processando...
                    </h4>
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      {progressInfo.current} de {progressInfo.total}
                    </span>
                  </div>

                  {/* Barra de progresso */}
                  <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                    ></div>
                  </div>

                  {progressInfo.currentEmail && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Processando: <strong>{progressInfo.currentEmail}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* Estatísticas em Tempo Real */}
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">
                  {isLoading ? 'Processando...' : 'Operação Concluída!'}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Total Processado</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.totalProcessed || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Senhas Atualizadas</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.passwordsUpdated || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Sucessos</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.successCount || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">Pulados</p>
                    <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                      {operationResults?.skippedCount || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Erros</p>
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                      {operationResults?.errorCount || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Clientes Pulados */}
              {operationResults?.skipped && operationResults.skipped.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                    Clientes Pulados:
                  </h4>
                  <ul className="space-y-2">
                    {operationResults.skipped.map((item, index) => (
                      <li key={index} className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>{item.email}:</strong> {item.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Erros */}
              {operationResults?.errors && operationResults.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-2">
                    Clientes com Erro:
                  </h4>
                  <ul className="space-y-2">
                    {operationResults.errors.map((error, index) => (
                      <li key={index} className="text-sm text-red-700 dark:text-red-300">
                        <strong>{error.email}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          {!isLoading && !showResults ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button onClick={executeOperation} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Executar
                  </>
                )}
              </Button>
            </>
          ) : showResults ? (
            <>{!isLoading && <Button onClick={handleClose}>Fechar</Button>}</>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
